import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import dayjs from 'dayjs'
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs'
import { useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import type { ListGuestsResponse } from '@/lib/api/client/types.gen'

type AdminGuestSuccessResponse = {
  data: ListGuestsResponse
}

import {
  deleteApiAdminGuestById,
  getApiAdminGuest,
  getApiGuestsGroups,
  postApiAdminGuestImport,
} from '@/lib/api/client'
import {
  getApiAdminGuestInfiniteQueryKey,
  getApiAdminGuestQueryKey,
  getApiGuestsGroupsQueryKey,
} from '@/lib/api/client/@tanstack/react-query.gen'
import { downloadGuestsCSV } from '@/lib/csv'

// Hook to fetch all unique guest groups
export const useGuestGroups = () => {
  const { data: groupsData, isLoading } = useQuery({
    queryKey: getApiGuestsGroupsQueryKey(),
    queryFn: () => getApiGuestsGroups(),
    staleTime: 5 * 60_000,
  })
  const groups = groupsData?.data || []
  return { groups, isLoading }
}

// 1. Standard Paginated Hook (for Desktop Table)
export const useMasterGuestList = ({
  enabled = true,
  limit = 10,
}: { enabled?: boolean; limit?: number } = {}) => {
  const queryClient = useQueryClient()
  const [page, setPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1).withOptions({ shallow: true }),
  )
  const [searchQuery, setSearchQuery] = useQueryState(
    'q',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery || '')

  // Filter States
  const [group, setGroup] = useQueryState(
    'group',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [side, setSide] = useQueryState(
    'side',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [status, setStatus] = useQueryState(
    'status',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [isTableAssigned, setIsTableAssigned] = useQueryState(
    'isTableAssigned',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [sortBy, setSortBy] = useQueryState(
    'sortBy',
    parseAsString.withDefault('fullName').withOptions({ shallow: true }),
  )
  const [sortOrder, setSortOrder] = useQueryState(
    'sortOrder',
    parseAsString.withDefault('asc').withOptions({ shallow: true }),
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Reset page to 1 when filters or query change
  useEffect(() => {
    setPage(1)
  }, [
    debouncedQuery,
    group,
    side,
    status,
    isTableAssigned,
    sortBy,
    sortOrder,
    setPage,
  ])

  const { data: guestsData, isLoading: isFetching } = useQuery({
    queryKey: getApiAdminGuestQueryKey({
      query: {
        q: debouncedQuery,
        page,
        limit,
        group: group || undefined,
        side: (side as 'groom' | 'bride') || undefined,
        status: (status as 'pending' | 'confirmed' | 'checked-in') || undefined,
        isTableAssigned: (isTableAssigned as 'true' | 'false') || undefined,
        sortBy:
          (sortBy as 'fullName' | 'status' | 'tableNumber' | 'createdAt') ||
          undefined,
        sortOrder: (sortOrder as 'asc' | 'desc') || undefined,
      },
    }),
    queryFn: () =>
      getApiAdminGuest({
        query: {
          q: debouncedQuery,
          page,
          limit,
          group: group || undefined,
          side: (side as 'groom' | 'bride') || undefined,
          status:
            (status as 'pending' | 'confirmed' | 'checked-in') || undefined,
          isTableAssigned: (isTableAssigned as 'true' | 'false') || undefined,
          sortBy:
            (sortBy as 'fullName' | 'status' | 'tableNumber' | 'createdAt') ||
            undefined,
          sortOrder: (sortOrder as 'asc' | 'desc') || undefined,
        },
      }),
    enabled,
  })

  const guests = guestsData?.data?.data || []
  const totalCount = guestsData?.data?.meta?.total || 0
  const totalPages = guestsData?.data?.meta?.totalPages || 0
  const isLoading = isFetching

  const removeGuest = async (id: string) => {
    try {
      await deleteApiAdminGuestById({
        path: { id },
      })
      await queryClient.invalidateQueries({
        queryKey: getApiAdminGuestQueryKey(),
      })
    } catch (error) {
      console.error('Failed to remove guest:', error)
      throw error
    }
  }

  const importGuests = async (
    file: File,
    sideMappings?: Record<string, 'groom' | 'bride'>,
    maritalStatusMappings?: Record<
      string,
      'single' | 'not_single' | 'prefer_not_to_say'
    >,
  ) => {
    try {
      await postApiAdminGuestImport({
        body: {
          file,
          mappings: JSON.stringify({
            side: sideMappings || {},
            maritalStatus: maritalStatusMappings || {},
          }),
        },
      })
      await queryClient.invalidateQueries({
        queryKey: getApiAdminGuestQueryKey(),
      })
    } catch (error) {
      console.error('Failed to import guests:', error)
      throw error
    }
  }

  const exportGuests = async () => {
    const res = await getApiAdminGuest({
      query: {
        limit: 1000,
        page: 1,
        q: debouncedQuery || undefined,
        group: group || undefined,
        side: (side as 'groom' | 'bride') || undefined,
        status: (status as 'pending' | 'confirmed' | 'checked-in') || undefined,
        isTableAssigned: (isTableAssigned as 'true' | 'false') || undefined,
        sortBy:
          (sortBy as 'fullName' | 'status' | 'tableNumber' | 'createdAt') ||
          undefined,
        sortOrder: (sortOrder as 'asc' | 'desc') || undefined,
      },
    })
    const allGuests = res.data?.data ?? []
    downloadGuestsCSV(allGuests)
  }

  return {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    group,
    setGroup,
    side,
    setSide,
    status,
    setStatus,
    isTableAssigned,
    setIsTableAssigned,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    guests,
    isLoading,
    totalCount,
    totalPages,
    page,
    setPage,
    removeGuest,
    importGuests,
    exportGuests,
  }
}

// 2. Infinite Scroll Hook (for Mobile & Dialogs)
export const useInfiniteMasterGuestList = ({
  enabled = true,
  limit = 12,
}: { enabled?: boolean; limit?: number } = {}) => {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useQueryState(
    'q',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery || '')

  // Filter States
  const [group, setGroup] = useQueryState(
    'group',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [side, setSide] = useQueryState(
    'side',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [status, setStatus] = useQueryState(
    'status',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [isTableAssigned, setIsTableAssigned] = useQueryState(
    'isTableAssigned',
    parseAsString.withDefault('').withOptions({ shallow: true }),
  )
  const [sortBy, setSortBy] = useQueryState(
    'sortBy',
    parseAsString.withDefault('fullName').withOptions({ shallow: true }),
  )
  const [sortOrder, setSortOrder] = useQueryState(
    'sortOrder',
    parseAsString.withDefault('asc').withOptions({ shallow: true }),
  )

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: getApiAdminGuestInfiniteQueryKey({
      query: {
        q: debouncedQuery,
        limit,
        group: group || undefined,
        side: (side as 'groom' | 'bride') || undefined,
        status: (status as 'pending' | 'confirmed' | 'checked-in') || undefined,
        isTableAssigned: (isTableAssigned as 'true' | 'false') || undefined,
        sortBy:
          (sortBy as 'fullName' | 'status' | 'tableNumber' | 'createdAt') ||
          undefined,
        sortOrder: (sortOrder as 'asc' | 'desc') || undefined,
      },
    }),
    queryFn: ({ pageParam = 1 }) =>
      getApiAdminGuest({
        query: {
          q: debouncedQuery,
          page: pageParam as number,
          limit,
          group: group || undefined,
          side: (side as 'groom' | 'bride') || undefined,
          status:
            (status as 'pending' | 'confirmed' | 'checked-in') || undefined,
          isTableAssigned: (isTableAssigned as 'true' | 'false') || undefined,
          sortBy:
            (sortBy as 'fullName' | 'status' | 'tableNumber' | 'createdAt') ||
            undefined,
          sortOrder: (sortOrder as 'asc' | 'desc') || undefined,
        },
      }),
    getNextPageParam: (lastPage: AdminGuestSuccessResponse) => {
      const { page, totalPages } = lastPage.data.meta
      return page < totalPages ? page + 1 : undefined
    },
    initialPageParam: 1,
    enabled,
  })

  const guests = useMemo(() => {
    return data?.pages.flatMap((page) => page.data.data) || []
  }, [data])

  const totalCount = data?.pages[0]?.data.meta.total || 0

  const { ref: loadMoreRef, inView } = useInView()

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  const removeGuest = async (id: string) => {
    try {
      await deleteApiAdminGuestById({
        path: { id },
      })
      await queryClient.invalidateQueries({
        queryKey: getApiAdminGuestQueryKey(),
      })
    } catch (error) {
      console.error('Failed to remove guest:', error)
      throw error
    }
  }

  const importGuests = async (
    file: File,
    sideMappings?: Record<string, 'groom' | 'bride'>,
    maritalStatusMappings?: Record<
      string,
      'single' | 'not_single' | 'prefer_not_to_say'
    >,
  ) => {
    try {
      await postApiAdminGuestImport({
        body: {
          file,
          mappings: JSON.stringify({
            side: sideMappings || {},
            maritalStatus: maritalStatusMappings || {},
          }),
        },
      })
      await queryClient.invalidateQueries({
        queryKey: getApiAdminGuestQueryKey(),
      })
    } catch (error) {
      console.error('Failed to import guests:', error)
      throw error
    }
  }

  const exportGuests = async () => {
    const res = await getApiAdminGuest({
      query: {
        limit: 1000,
        page: 1,
        q: debouncedQuery || undefined,
        group: group || undefined,
        side: (side as 'groom' | 'bride') || undefined,
        status: (status as 'pending' | 'confirmed' | 'checked-in') || undefined,
        isTableAssigned: (isTableAssigned as 'true' | 'false') || undefined,
        sortBy:
          (sortBy as 'fullName' | 'status' | 'tableNumber' | 'createdAt') ||
          undefined,
        sortOrder: (sortOrder as 'asc' | 'desc') || undefined,
      },
    })
    const allGuests = res.data?.data ?? []
    const timestamp = dayjs().format('YYYYMMDDHHmmss')
    downloadGuestsCSV(allGuests, `guests_export_${timestamp}.csv`)
  }

  return {
    searchQuery,
    setSearchQuery,
    debouncedQuery,
    group,
    setGroup,
    side,
    setSide,
    status,
    setStatus,
    isTableAssigned,
    setIsTableAssigned,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    guests,
    isLoading,
    isFetchingNextPage,
    totalCount,
    loadMoreRef,
    handleRefresh: refetch,
    removeGuest,
    importGuests,
    exportGuests,
  }
}
